import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, Wrench } from "lucide-react";
import { NAV_LINKS } from "../../data/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    const el = document.getElementById(target);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
        <button
          data-testid="nav-logo"
          onClick={() => go("hero")}
          className="group flex items-center gap-3 text-left"
        >
          <span className="flex h-9 w-9 items-center justify-center border border-[#FF2A2A] text-[#FF2A2A] transition-colors duration-300 group-hover:bg-[#FF2A2A] group-hover:text-white">
            <Wrench className="h-4 w-4" strokeWidth={2} />
          </span>
          <span className="font-display text-lg font-bold uppercase leading-none tracking-tight text-[#FAFAFA]">
            Ciro Natale
            <span className="block font-mono-accent text-[9px] tracking-[0.25em] text-[#FF2A2A]">
              EST. 1972
            </span>
          </span>
        </button>

        <ul className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((l) => (
            <li key={l.target}>
              <button
                data-testid={`nav-link-${l.target}`}
                onClick={() => go(l.target)}
                className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] transition-colors duration-300 hover:text-[#FAFAFA]"
              >
                {l.label}
              </button>
            </li>
          ))}
          <li>
            <button
              data-testid="nav-cta-prenota"
              onClick={() => go("contatti")}
              className="bg-[#FF2A2A] px-5 py-2.5 font-display text-sm font-semibold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-white hover:text-black"
            >
              Prenota
            </button>
          </li>
        </ul>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="text-[#FAFAFA] lg:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/95 backdrop-blur-xl lg:hidden"
          >
            <ul className="flex flex-col px-5 py-4">
              {NAV_LINKS.map((l) => (
                <li key={l.target} className="border-b border-white/5">
                  <button
                    data-testid={`nav-mobile-link-${l.target}`}
                    onClick={() => go(l.target)}
                    className="w-full py-4 text-left font-display text-xl uppercase tracking-tight text-[#FAFAFA]"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
              <li className="pt-4">
                <button
                  data-testid="nav-mobile-cta"
                  onClick={() => go("contatti")}
                  className="w-full bg-[#FF2A2A] py-3 font-display uppercase tracking-wide text-white"
                >
                  Prenota un appuntamento
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
